// ============================================================================
// CLIENT TYPE HOOKS
// ============================================================================
// React hooks for the ClientType master.
//
// Pattern follows useEngagementTypes.ts:
// - useClientTypes(params)            → list with pagination, refetch
// - useClientType(id)                 → single record by id
// - useCreateClientType()             → create mutation
// - useUpdateClientType()             → update mutation
// - useDeleteClientType()             → delete mutation
//
// Note: useClientTypes serializes params via JSON.stringify in the dep
// array to avoid infinite re-render when callers pass an inline object.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { clientTypeService } from 'api/services/clientTypeService';
import type {
  ID,
  PaginatedResponse,
  ClientType,
  CreateClientTypeRequest,
  UpdateClientTypeRequest,
  ClientTypeSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of client types.
 * Refetches automatically when `params` changes.
 */
export const useClientTypes = (params?: ClientTypeSearchRequest) => {
  const [data, setData] = useState<ClientType[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<ClientType>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Serialize params so a new object reference each render doesn't re-fire the effect
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchClientTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await clientTypeService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchClientTypes();
  }, [fetchClientTypes]);

  return {
    data,
    pagination,
    loading,
    error,
    refetch: fetchClientTypes,
  };
};

/**
 * Hook to fetch a single client type by id.
 */
export const useClientType = (id: ID | null | undefined) => {
  const [data, setData] = useState<ClientType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchClientType = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await clientTypeService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClientType();
  }, [fetchClientType]);

  return { data, loading, error, refetch: fetchClientType };
};

/**
 * Hook to create a new client type.
 */
export const useCreateClientType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createClientType = async (
    data: CreateClientTypeRequest,
    options?: {
      onSuccess?: (item: ClientType) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await clientTypeService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createClientType, loading, error, success };
};

/**
 * Hook to update a client type. Always pass the latest rowVersion.
 */
export const useUpdateClientType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateClientType = async (
    id: ID,
    data: UpdateClientTypeRequest,
    options?: {
      onSuccess?: (item: ClientType) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await clientTypeService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateClientType, loading, error, success };
};

/**
 * Hook to delete a client type (soft-delete on the server).
 */
export const useDeleteClientType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteClientType = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await clientTypeService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteClientType, loading, error, success };
};
